import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  FlakyStep,
  FlakyTest,
  FlakyTestsSummary,
  GcbId,
  LassoBuild,
  LassoBuildLogResponse,
  LassoTestAttempt,
  LassoTestHistoryEntry,
  LassoTraceViewerUrlResponse,
  TestHistorySummary,
} from '@internal/plugin-lasso-common';
import type {
  AttemptsParams,
  BuildsParams,
  FlakyStepsParams,
  FlakyTestsParams,
  FlakyTestsSummaryParams,
  LassoApi,
  StacksParams,
  StatusesParams,
  TestHistoryParams,
  TestHistorySummaryParams,
} from './LassoApi';

function buildQuery(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  if (entries.length === 0) {
    return '';
  }
  const search = new URLSearchParams();
  for (const [key, value] of entries) {
    search.set(key, String(value));
  }
  return `?${search.toString()}`;
}

// Thrown by fetchJson on a non-2xx response; carries the HTTP status so callers
// can branch on it (e.g. 404 → absent resource) without matching the message.
export class LassoFetchError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'LassoFetchError';
  }
}

export class LassoClient implements LassoApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async fetchJson<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('lasso');
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!res.ok) {
      const text = await res.text();
      throw new LassoFetchError(
        res.status,
        `Request failed: ${res.status} ${text}`,
      );
    }
    return res.json() as Promise<T>;
  }

  async getBuilds(params: BuildsParams = {}): Promise<LassoBuild[]> {
    const data = await this.fetchJson<{ builds: LassoBuild[] }>(
      `/builds${buildQuery({ ...params })}`,
    );
    return data.builds;
  }

  async getBuild(buildId: string): Promise<LassoBuild | null> {
    const data = await this.fetchJson<{ build: LassoBuild | null }>(
      `/builds/${encodeURIComponent(buildId)}`,
    );
    return data.build;
  }

  async getBuildAttempts(
    buildId: string,
    params: AttemptsParams = {},
  ): Promise<LassoTestAttempt[]> {
    const data = await this.fetchJson<{ attempts: LassoTestAttempt[] }>(
      `/builds/${encodeURIComponent(buildId)}/attempts${buildQuery({
        ...params,
      })}`,
    );
    return data.attempts;
  }

  async getTestHistory(
    params: TestHistoryParams,
  ): Promise<LassoTestHistoryEntry[]> {
    const data = await this.fetchJson<{ attempts: LassoTestHistoryEntry[] }>(
      `/tests/history${buildQuery({ ...params })}`,
    );
    return data.attempts;
  }

  async getTestHistorySummary(
    params: TestHistorySummaryParams,
  ): Promise<TestHistorySummary> {
    const data = await this.fetchJson<{ summary: TestHistorySummary }>(
      `/tests/history/summary${buildQuery({ ...params })}`,
    );
    return data.summary;
  }

  async getFlakyTests(params: FlakyTestsParams = {}): Promise<FlakyTest[]> {
    const data = await this.fetchJson<{ tests: FlakyTest[] }>(
      `/tests/flaky${buildQuery({ ...params })}`,
    );
    return data.tests;
  }

  async getFlakyTestsSummary(
    params: FlakyTestsSummaryParams = {},
  ): Promise<FlakyTestsSummary> {
    const data = await this.fetchJson<{ summary: FlakyTestsSummary }>(
      `/tests/flaky/summary${buildQuery({ ...params })}`,
    );
    return data.summary;
  }

  async getFlakySteps(params: FlakyStepsParams = {}): Promise<FlakyStep[]> {
    const data = await this.fetchJson<{ steps: FlakyStep[] }>(
      `/tests/flaky/steps${buildQuery({ ...params })}`,
    );
    return data.steps;
  }

  async getTraceViewerUrl(
    testAttemptId: string,
  ): Promise<LassoTraceViewerUrlResponse> {
    return this.fetchJson<LassoTraceViewerUrlResponse>(
      `/traces/${encodeURIComponent(testAttemptId)}/viewer-url`,
    );
  }

  async getAttempt(testAttemptId: string): Promise<LassoTestAttempt> {
    const data = await this.fetchJson<{ attempt: LassoTestAttempt }>(
      `/attempts/${encodeURIComponent(testAttemptId)}`,
    );
    return data.attempt;
  }

  async getStacks(params: StacksParams = {}): Promise<string[]> {
    const data = await this.fetchJson<{ stacks: string[] }>(
      `/stacks${buildQuery({ ...params })}`,
    );
    return data.stacks;
  }

  async getStatuses(params: StatusesParams = {}): Promise<(string | null)[]> {
    const data = await this.fetchJson<{ statuses: (string | null)[] }>(
      `/statuses${buildQuery({ ...params })}`,
    );
    return data.statuses;
  }

  async getBuildLog(
    buildId: string,
    gcbId: GcbId,
  ): Promise<LassoBuildLogResponse | null> {
    try {
      return await this.fetchJson<LassoBuildLogResponse>(
        `/builds/${encodeURIComponent(buildId)}/log?gcbId=${encodeURIComponent(
          gcbId,
        )}`,
      );
    } catch (err) {
      // 404 = no log (local run / aged out): an absent log, not an error.
      if (err instanceof LassoFetchError && err.status === 404) {
        return null;
      }
      throw err;
    }
  }
}
